import { getDoseComponents } from "@/lib/dose-components";

import { Unit } from "@/types/enums/unit";
import { ORAL_MEDICATION_NAMES } from "@/types/enums/weight-loss-medications";
import {
  DoseComponent,
  Medication,
  MedicationSummary,
  MedicationUpdate,
} from "@/types/medication";

import {
  DoseComponentFormValues,
  DoseFormValues,
  componentsAreOral,
} from "./dose-form-schema";
import {
  DoseLotInventory,
  serializeDoseAllocations,
} from "./dose-lot-resolution";
import { ShipmentLot } from "@/query/medications/medications";

const componentFromDoseComponent = (
  component: DoseComponent,
): DoseComponentFormValues => ({
  id: component.id,
  medicationId: component.medicationId,
  amount: component.amount !== undefined ? Number(component.amount) : 0,
  unit: (component.unit as Unit) || Unit.MG,
  lotSelection: component.lots?.some((lot) => !lot.shipmentId && lot.lotNumber)
    ? {
        type: "lot",
        value: component.lots
          .filter((lot) => !lot.shipmentId)
          .map((lot) => lot.lotNumber)
          .filter(Boolean)
          .join("\n"),
      }
    : null,
  name: component.name || "",
  quantity: component.quantity ?? 1,
  dilutionId: component.dilutionId ?? null,
  inventoryBaseline: {
    medicationId: component.medicationId,
    stockId: component.stockId || undefined,
    hasTrackedInventory:
      !!component.lots?.some((lot) => lot.shipmentId) ||
      !!component.stockId ||
      !!component.inventoryLots?.some((lot) => lot.quantity > 0),
    recordedLots: component.lots ?? [],
    automaticShipmentIds: (component.lots ?? [])
      .filter((lot) => lot.selectionMethod === "AUTOMATIC")
      .flatMap((lot) => (lot.shipmentId ? [lot.shipmentId] : [])),
    untrackedLots: (component.lots ?? [])
      .filter((lot) => !lot.shipmentId)
      .flatMap((lot) => (lot.lotNumber ? [lot.lotNumber] : [])),
    quantity: component.quantity ?? 1,
    amount: component.amount !== undefined ? Number(component.amount) : 0,
    unit: (component.unit as Unit) || Unit.MG,
    dilutionId: component.dilutionId ?? null,
  },
});

// The dose's locations as form rows. A dose that only has the plain location
// text shows it as one row, so editing in the list never drops it.
const locationRowsFromDose = (
  medication: MedicationSummary,
): { label: string; amount: string }[] => {
  const locations = medication.locations ?? [];
  if (locations.length > 0) {
    return locations.map((location) => ({
      label: location.label,
      amount: location.amount != null ? String(location.amount) : "",
    }));
  }
  // Always one row to type into, like the single box it replaces. Text typed
  // before locations were a list stays whole: "Left deltoid, IM" is one place.
  return [{ label: medication.administrationLocation ?? "", amount: "" }];
};

export const getEditDoseFormValues = (
  medication: MedicationSummary,
): DoseFormValues => ({
  components: getDoseComponents(medication).map(componentFromDoseComponent),
  administrationLocation: medication.administrationLocation || "",
  locations: locationRowsFromDose(medication),
  sideEffects: medication.sideEffects || "",
  doseDate: medication.doseDate ? new Date(medication.doseDate) : new Date(),
  comment: medication.comment || "",
  frequency: medication.frequency || "",
  providerId: medication.provider?.id,
  managementStatus: medication.managementStatus ?? null,
  compoundMedicationId: medication.compoundMedicationId ?? null,
});

const identityMatches = (
  component: DoseComponentFormValues,
  original?: DoseComponent,
) =>
  !!original &&
  component.name === original.name &&
  component.unit === ((original.unit as Unit | null) || Unit.MG);

export function getOriginalComponentsById(
  medication: MedicationSummary,
): ReadonlyMap<string, DoseComponent> {
  return new Map(
    getDoseComponents(medication).flatMap((component) =>
      component.id ? [[component.id, component] as const] : [],
    ),
  );
}

export const findMedicationForEditComponent = (
  component: DoseComponentFormValues,
  medications: Medication[],
): Medication | undefined => {
  // An explicit saved/selected ID must never silently become a same-name product.
  // Missing historical catalog metadata is not a different medication selection.
  if (component.medicationId) {
    return medications.find(
      (medication) => medication.id === component.medicationId,
    );
  }

  return (
    medications.find(
      (medication) =>
        medication.name === component.name &&
        (!component.unit || medication.unit === component.unit),
    ) ?? medications.find((medication) => medication.name === component.name)
  );
};

/** Inventory can verify saved history without pretending it is active catalog metadata. */
export function getDoseInventoryMedicationId(
  component: DoseComponentFormValues,
  selectedMedication?: Medication,
): string | undefined {
  return (
    selectedMedication?.id ??
    (component.id &&
    component.medicationId &&
    component.inventoryBaseline?.medicationId === component.medicationId
      ? component.medicationId
      : undefined)
  );
}

interface EditComponentPayloadOptions {
  component: DoseComponentFormValues;
  original?: DoseComponent;
  selectedMedication?: Medication;
  inventory: DoseLotInventory;
}

const toComponentPayload = ({
  component,
  original,
  selectedMedication,
  inventory,
}: EditComponentPayloadOptions): DoseComponent => {
  const isOral = ORAL_MEDICATION_NAMES.includes(component.name);
  const identityIsUnchanged = identityMatches(component, original);
  const originalUntracked = (original?.lots ?? [])
    .filter((lot) => !lot.shipmentId)
    .flatMap((lot) => (lot.lotNumber ? [lot.lotNumber] : []));
  const typedLots = (component.lotSelection?.value ?? "")
    .split("\n")
    .map((value) => value.trim())
    .filter(Boolean);
  const carriesOriginalLots =
    !identityIsUnchanged &&
    JSON.stringify(typedLots) === JSON.stringify(originalUntracked);
  const untrackedLots = isOral || carriesOriginalLots ? [] : typedLots;
  const lotIsUnchanged =
    identityIsUnchanged &&
    JSON.stringify(untrackedLots) === JSON.stringify(originalUntracked);
  const explicitAllocations = serializeDoseAllocations(component, inventory);
  const shipmentAllocations =
    explicitAllocations ??
    (!inventory.preview?.allowsClinicalLotReferences &&
    (original?.stockId || original?.lots?.some((lot) => lot.shipmentId)) &&
    (isOral || !identityIsUnchanged)
      ? []
      : undefined);

  const payloadMedicationId = identityIsUnchanged
    ? component.medicationId
    : selectedMedication?.id;
  // A dilution only travels with the medication it belongs to; otherwise the API
  // starts the new medication on its default.
  const sendsDilution =
    component.dilutionId === null ||
    (payloadMedicationId === component.medicationId &&
      ((payloadMedicationId === original?.medicationId &&
        component.dilutionId === original?.dilutionId) ||
        !!selectedMedication?.dilutions?.some(
          (dilution) => dilution.id === component.dilutionId,
        )));

  return {
    id: component.id,
    medicationId: payloadMedicationId,
    name: component.name,
    amount: component.amount,
    unit: component.unit,
    quantity: component.quantity,
    ...(sendsDilution && { dilutionId: component.dilutionId ?? null }),
    ...(!lotIsUnchanged && { untrackedLots }),
    shipmentAllocations,
    ...(!isOral &&
      component.clinicalShipmentIds !== undefined && {
        clinicalShipmentIds: component.clinicalShipmentIds,
      }),
    ...(component.lotAllocations !== undefined &&
      !isOral && { reuseCheckoutInventory: component.checkoutReuse === true }),
    ...(component.lotAllocations !== undefined &&
      component.correctActualInventoryUse && {
        correctActualInventoryUse: true,
      }),
  };
};

export interface EditDoseInventorySnapshot extends DoseLotInventory {
  medicationId?: string;
  shipments: ShipmentLot[];
}

interface EditDosePayloadOptions {
  medication: MedicationSummary;
  medications: Medication[];
  inventory: EditDoseInventorySnapshot[];
  canChangeProvider: boolean;
  /** The location list was shown (the clinic records doses by location). */
  editLocations?: boolean;
}

export function toEditDosePayload(
  values: DoseFormValues,
  {
    medication,
    medications,
    inventory,
    canChangeProvider,
    editLocations = false,
  }: EditDosePayloadOptions,
): MedicationUpdate {
  const originalsById = getOriginalComponentsById(medication);
  const components = values.components.map((component, index) => {
    const original = component.id ? originalsById.get(component.id) : undefined;
    const selectedMedication = findMedicationForEditComponent(
      component,
      medications,
    );
    const inventoryEntry = inventory[index];
    if (
      component.lotAllocations !== undefined &&
      (inventoryEntry?.compoundMedicationId ?? null) !==
        (values.compoundMedicationId ?? null)
    )
      throw new Error(
        "Compound context changed. Review the dose inventory before saving.",
      );
    return toComponentPayload({
      component,
      original,
      selectedMedication,
      inventory: inventoryEntry ?? { shipments: [] },
    });
  });

  const payload: MedicationUpdate = {
    components,
    administrationLocation: componentsAreOral(values.components)
      ? ""
      : values.administrationLocation,
    sideEffects: values.sideEffects,
    doseDate: values.doseDate?.toISOString(),
    comment: values.comment,
    frequency: values.frequency,
    compoundMedicationId: values.compoundMedicationId ?? null,
  };

  // Locations are sent only when the rows differ from what the dose opened
  // with, so an edit to anything else leaves them exactly as they are.
  if (editLocations) {
    // "20" retyped as "20.0" is not a change.
    const sameAmount = (a: string, b: string) =>
      a === b || (a !== "" && b !== "" && Number(a) === Number(b));
    const cleanRows = (rows: { label: string; amount: string }[]) =>
      rows
        .map((row) => ({ label: row.label.trim(), amount: row.amount.trim() }))
        .filter((row) => row.label !== "");
    const rows = componentsAreOral(values.components)
      ? []
      : cleanRows(values.locations ?? []);
    const opened = cleanRows(locationRowsFromDose(medication));
    const changed =
      rows.length !== opened.length ||
      rows.some(
        (row, index) =>
          row.label !== opened[index].label ||
          !sameAmount(row.amount, opened[index].amount),
      );
    if (changed) {
      const musclesByLabel = new Map(
        (medication.locations ?? []).map((location) => [
          location.label,
          location.muscle ?? null,
        ]),
      );
      payload.locations = rows.map((row) => ({
        label: row.label,
        amount: row.amount === "" ? null : Number(row.amount),
        // A row keeps its muscle while its name is unchanged.
        muscle: musclesByLabel.get(row.label) ?? null,
      }));
      payload.administrationLocation = rows.map((row) => row.label).join(", ");
    } else {
      payload.administrationLocation = medication.administrationLocation ?? "";
    }
  }

  if (values.managementStatus != null) {
    payload.managementStatus = values.managementStatus;
  }

  if (canChangeProvider && values.providerId !== medication.provider?.id) {
    payload.providerId = values.providerId;
  }

  return payload;
}
